import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Router } from '@angular/router';
import { CanComponentDeactivate } from '@klickdata/core/auth';
import { Customer, CustomerService } from '@klickdata/core/customer';
import { MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { MobileService } from '@klickdata/core/mobile';
import { Filter, GlobalFilterProperty, SelectFilterOption } from '@klickdata/core/table';
import { Utils } from '@klickdata/core/util';
import { filter, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { ResourcesSelectClusterComponent } from '../resources-select-cluster/resources-select-cluster.component';
import { SelectModelBaseDirective } from '../select-model-base/select-model-base.component';
import { CustomerSelectDatatableService } from './customer-select-datatable.service';
import { AppScope } from '@klickdata/core/resource';
import { SectionSelectSheetComponent } from '../section-select/section-select-sheet/section-select-sheet.component';

@Component({
    selector: 'app-customer-select',
    templateUrl: './customer-select.component.html',
    styleUrls: ['./customer-select.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [CustomerSelectDatatableService],
})
export class CustomerSelectComponent
    extends SelectModelBaseDirective<Customer>
    implements OnInit, AfterViewInit, CanComponentDeactivate, OnDestroy
{
    @Input() accessForm: FormGroup;
    @Input() referenceFilter: string;
    @Input() selectionFilters: Filter<any>[];
    public columns = ['select', 'name', 'tools'];
    @Input() public sync_all_key = 'sync_all_customers';
    @Input() public attachKey = 'customers_attach';
    @Input() public detachKey = 'customers_detach';
    @Input() customerId: number;
    @Input() modelSpecs: AppScope[] = [AppScope.RESOURCES];
    public itemSelectedOptions: SelectFilterOption[];
    GlobalFilterProperty = GlobalFilterProperty;
    AppScope = AppScope;

    constructor(
        protected datatableService: CustomerSelectDatatableService,
        protected mobileService: MobileService,
        private router: Router,
        private customerService: CustomerService,
        private bottomSheet: MatBottomSheet,
        private message: MessageService
    ) {
        super(datatableService, mobileService);
        this.itemSelectedOptions = Utils.getItemSelectedOptions();
    }

    protected refresh(id: any): void {
        const appliedFilters = [];
        if (this.selectionFilters && this.selectionFilters.length > 0) {
            this.selectionFilters.forEach((filterItem) => {
                appliedFilters.push(new Filter(filterItem.property, filterItem.items));
            });
        } else {
            appliedFilters.push({ property: this.referenceFilter, items: [id] });
        }
        this.filter.createOrUpdateFilter(appliedFilters);
    }
    editCustomer(customer: Customer) {
        this.router.navigate(['/master/customers/customer-manager', customer.id, 'resources']);
    }
    specifyCustomerSections(customer: Customer) {
        const sheet = this.bottomSheet.open(SectionSelectSheetComponent, {
            data: {
                id: customer.id,
                title: customer.name,
                referenceFilter: 'referenceCluster',
                context: $localize`:@@sections:Sections`,
                referencePropertyValue: `${this.customerId}|${customer.id}`,
                contextIcon: 'widgets',
                scope: 'cluster',
            },
            panelClass: 'sheet-wrapper',
        });
        const sub = sheet.instance.submit
            .pipe(
                filter((data) => !!data?.sections_attach?.length || !!data?.sections_detach?.length),
                switchMap((data) =>
                    this.customerService.update({
                        id: this.customerId,
                        cluster: { customer_id: customer.id, sections: this.getSectionsData(data) },
                    })
                ),
                takeUntil(this.destroy)
            )
            .subscribe(() => {
                sheet.instance.updateForm();
            });
        sheet
            .afterDismissed()
            .pipe(takeUntil(this.destroy))
            .subscribe(() => {
                sub.unsubscribe();
            });
    }
    specifyCustomerResources(customer: Customer) {
        this.bottomSheet
            .open(ResourcesSelectClusterComponent, {
                data: {
                    customer: customer,
                    referencePropertyValue: `${this.customerId}|${customer.id}`,
                    referenceProperty: 'referenceCluster',
                },
                panelClass: 'sheet-wrapper',
            })
            .afterDismissed()
            .pipe(
                takeUntil(this.destroy),
                filter((data) => !!data),
                switchMap((data) =>
                    this.customerService.update({
                        id: this.customerId,
                        cluster: { customer_id: customer.id, resources: data },
                    })
                )
            )
            .subscribe((res) => {
                this.message.openMessage(MessageSavedComponent);
            });
    }
    private getSectionsData(data: any) {
        const sectionData: { attach_ids?: any[]; detach_ids?: any[] } = {};
        if (!!data.sections_attach?.length) {
            sectionData.attach_ids = data.sections_attach;
        }
        if (!!data.sections_detach?.length) {
            sectionData.detach_ids = data.sections_detach;
        }
        return sectionData;
    }
}
